import React, { useState, useEffect } from "react";
import axios from "axios";
import { useToast } from "@/hooks/use-toast";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

export function BackupCodesManager({ authToken }) {
  const { toast } = useToast();
  const [remainingCount, setRemainingCount] = useState(null);
  const [newCodes, setNewCodes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [copied, setCopied] = useState(false);

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${authToken}` },
  });

  useEffect(() => {
    loadBackupCodesCount();
  }, [authToken]);

  const loadBackupCodesCount = async () => {
    if (!authToken) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/auth/backup-codes`, authHeaders());
      setRemainingCount(res.data.remaining_count);
    } catch (e) {
      console.error("Fehler beim Laden der Backup-Codes:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateCodes = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API_BASE}/api/auth/generate-backup-codes`, {}, authHeaders());
      setNewCodes(res.data.codes);
      setRemainingCount(res.data.remaining_count);
      setShowConfirm(false);
      toast({ title: "Neue Notfall-Codes generiert" });
    } catch (e) {
      console.error("Fehler beim Generieren:", e);
      toast({ title: "Fehler beim Generieren", variant: "destructive" });
    } finally {
      setGenerating(false);
    }
  };

  const handleCopyCodes = () => {
    if (!newCodes) return;
    const text = newCodes.join("\n");
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast({ title: "Codes in Zwischenablage kopiert" });
  };

  const handleCloseCodes = () => {
    if (!confirm("Haben Sie die Codes sicher gespeichert? Sie werden nicht erneut angezeigt!")) {
      return;
    }
    setNewCodes(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Status-Anzeige */}
      <div className={`rounded-xl p-4 ${
        remainingCount === null 
          ? "bg-slate-100" 
          : remainingCount === 0 
            ? "bg-red-50 border border-red-200" 
            : remainingCount <= 3 
              ? "bg-amber-50 border border-amber-200" 
              : "bg-emerald-50 border border-emerald-200"
      }`}>
        <div className="flex items-center gap-4">
          <div className={`flex h-12 w-12 items-center justify-center rounded-full ${
            remainingCount === null 
              ? "bg-slate-200" 
              : remainingCount === 0 
                ? "bg-red-100" 
                : remainingCount <= 3 
                  ? "bg-amber-100" 
                  : "bg-emerald-100"
          }`}>
            <svg className={`h-6 w-6 ${
              remainingCount === null 
                ? "text-slate-500" 
                : remainingCount === 0 
                  ? "text-red-600" 
                  : remainingCount <= 3 
                    ? "text-amber-600" 
                    : "text-emerald-600"
            }`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
          </div>
          <div className="flex-1">
            <p className="font-semibold text-slate-900">
              {remainingCount === null 
                ? "Keine Notfall-Codes" 
                : remainingCount === 0 
                  ? "Keine Notfall-Codes mehr!" 
                  : `${remainingCount} Notfall-Code${remainingCount !== 1 ? "s" : ""} übrig`
              }
            </p>
            <p className="text-sm text-slate-600">
              {remainingCount === null || remainingCount === 0
                ? "Generieren Sie neue Codes für den Notfall-Zugang"
                : remainingCount <= 3
                  ? "Bitte generieren Sie bald neue Codes"
                  : "Codes sind sicher gespeichert"
              }
            </p>
          </div>
        </div>
      </div>

      {/* Neue Codes anzeigen */}
      {newCodes && (
        <div className="rounded-xl border-2 border-blue-200 bg-blue-50 p-6">
          <div className="flex items-start gap-3 mb-4">
            <svg className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <p className="font-bold text-blue-900">Ihre neuen Notfall-Codes</p>
              <p className="text-sm text-blue-700">Speichern Sie diese Codes JETZT sicher ab. Sie werden nur einmal angezeigt!</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 mb-4 bg-white rounded-lg p-4 font-mono text-sm">
            {newCodes.map((code, index) => (
              <div key={index} className="flex items-center gap-2">
                <span className="text-slate-400 w-5">{index + 1}.</span>
                <span className="font-semibold tracking-wider">{code}</span>
              </div>
            ))}
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleCopyCodes}
              className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              {copied ? "Kopiert!" : "Alle kopieren"}
            </button>
            <button
              onClick={handleCloseCodes}
              className="rounded-lg bg-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-300"
            >
              Fertig
            </button>
          </div>
        </div>
      )}

      {/* Aktionen */}
      {!newCodes && (
        <>
          {showConfirm ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
              <p className="font-medium text-amber-800 mb-3">
                Sind Sie sicher? Alle bisherigen Notfall-Codes werden ungültig!
              </p>
              <div className="flex gap-2">
                <button
                  onClick={handleGenerateCodes}
                  disabled={generating}
                  className="flex-1 rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
                >
                  {generating ? "Generiere..." : "Ja, neue Codes generieren"}
                </button>
                <button
                  onClick={() => setShowConfirm(false)}
                  className="rounded-lg bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 border border-slate-200"
                >
                  Abbrechen
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => remainingCount > 0 ? setShowConfirm(true) : handleGenerateCodes()}
              disabled={generating}
              className="w-full rounded-lg bg-slate-900 px-4 py-3 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
            >
              {remainingCount === null || remainingCount === 0 
                ? "Notfall-Codes generieren" 
                : "Neue Notfall-Codes generieren"
              }
            </button>
          )}
        </>
      )}

      {/* Info */}
      <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-600 space-y-2">
        <p><strong>Was sind Notfall-Codes?</strong></p>
        <ul className="list-disc list-inside space-y-1 text-slate-500">
          <li>Einmalig verwendbare Codes für den Login ohne E-Mail-Zugang</li>
          <li>Speichern Sie diese in Ihrem Passwort-Manager</li>
          <li>Jeder Code kann nur einmal verwendet werden</li>
          <li>Bei Neugenerierung werden alle alten Codes ungültig</li>
        </ul>
      </div>
    </div>
  );
}
